import { Data } from '@blade/std-lib'
import { Result } from '@blade/std-lib'

const scriptCache = new Map<string, Promise<LoadScriptResult>>()

export type LoadScriptResult = Result.Result<LoadScriptData, LoadScriptError>

interface LoadScriptOptions {
  async?: boolean
  defer?: boolean
  module?: boolean
  attrs?: Record<string, string>
}

export class LoadScriptError extends Data.TaggedError('LoadScriptError')<{
  src: string
  cause: ErrorEvent | string | Event
}> {}

export class LoadScriptData extends Data.TaggedClass('LoadScriptData')<{
  src: string
  event?: Event
  script: HTMLScriptElement
}> {}

export function loadScript(
  src: string,
  options: LoadScriptOptions = {},
): Promise<LoadScriptResult> {
  const cached = scriptCache.get(src)

  if (cached) {
    return cached
  }

  const promise = new Promise<LoadScriptResult>((resolve) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${src}"]`,
    )

    if (existing) {
      if (existing.dataset.loaded === 'true') {
        resolve(Result.succeed(new LoadScriptData({ src, script: existing })))

        return
      }

      existing.addEventListener(
        'load',
        (event) =>
          resolve(
            Result.succeed(
              new LoadScriptData({ src, event, script: existing }),
            ),
          ),
        {
          once: true,
        },
      )

      existing.addEventListener(
        'error',
        (cause) =>
          resolve(
            Result.fail(
              new LoadScriptError({
                src,
                cause,
              }),
            ),
          ),
        { once: true },
      )

      return
    }

    const script = document.createElement('script')

    script.src = src
    script.async = options.async ?? true
    script.defer = options.defer ?? false

    if (options.module) {
      script.type = 'module'
    }

    if (options.attrs) {
      for (const [key, value] of Object.entries(options.attrs)) {
        script.setAttribute(key, value)
      }
    }

    script.onload = (event) => {
      script.dataset.loaded = 'true'

      resolve(Result.succeed(new LoadScriptData({ src, event, script })))
    }

    script.onerror = (cause) => {
      scriptCache.delete(src)

      resolve(
        Result.fail(
          new LoadScriptError({
            src,
            cause,
          }),
        ),
      )
    }

    document.head.appendChild(script)
  })

  scriptCache.set(src, promise)

  return promise
}
